import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import { Button, Tooltip, Upload, message } from 'antd'
import { UploadOutlined } from '@ant-design/icons'

import {
  CREATE_DIRECT_UPLOAD_URL_MUTATION,
  IMPORT_BASKET_SIZE_RATES_MUTATION,
  formatMutationErrors
} from './graphql'

const XLSX_ACCEPT = '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const ImportBasketSizeRatesButton = ({ onEnqueued }) => {
  const { t } = useTranslation('customerShippingRates')
  const [uploading, setUploading] = useState(false)

  const [createDirectUploadUrl] = useMutation(CREATE_DIRECT_UPLOAD_URL_MUTATION)
  const [importBasketSizeRates] = useMutation(IMPORT_BASKET_SIZE_RATES_MUTATION)

  const importFile = async (file) => {
    const { data: presignData } = await createDirectUploadUrl({ variables: { filename: file.name } })
    const presign = presignData && presignData.createDirectUploadUrl
    if (!presign || !presign.directUploadUrl || !presign.s3ObjectKey) throw new Error(t('import.failed'))

    const response = await fetch(presign.directUploadUrl, {
      method: 'PUT',
      headers: presign.contentType ? { 'Content-Type': presign.contentType } : undefined,
      body: file
    })
    if (!response.ok) throw new Error(t('import.failed'))

    const { data } = await importBasketSizeRates({
      variables: { objectKey: presign.s3ObjectKey, filename: file.name }
    })
    const result = data && data.importBasketSizeRates && data.importBasketSizeRates.result
    if (!result || !result.enqueued) {
      throw new Error(formatMutationErrors(result && result.errors) || t('import.failed'))
    }
  }

  const handleBeforeUpload = (file) => {
    setUploading(true)
    importFile(file)
      .then(() => {
        message.success(t('import.enqueued'), 6)
        onEnqueued()
      })
      .catch(error => message.error(error.message || t('import.failed')))
      .finally(() => setUploading(false))

    return Upload.LIST_IGNORE
  }

  return (
    <Tooltip title={t('import.hint')}>
      <Upload accept={XLSX_ACCEPT} beforeUpload={handleBeforeUpload} disabled={uploading} showUploadList={false}>
        <Button icon={<UploadOutlined />} loading={uploading}>
          {uploading ? t('import.uploading') : t('import.button')}
        </Button>
      </Upload>
    </Tooltip>
  )
}

ImportBasketSizeRatesButton.propTypes = {
  onEnqueued: PropTypes.func
}

ImportBasketSizeRatesButton.defaultProps = {
  onEnqueued: () => {}
}

export default ImportBasketSizeRatesButton
